import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { isNativeApp } from '../../utils/platform';

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [userType, setUserType] = useState('rider');
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    rememberMe: false
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(
      formData.username,
      formData.password,
      userType,
      formData.rememberMe
    );

    if (result.success) {
      // Check if there's an intended driver to redirect to
      const intendedDriverId = sessionStorage.getItem('intended_driver_id');
      
      if (intendedDriverId && result.userType === 'rider') {
        // Redirect to booking page with driver
        navigate(`/request-ride?driver_id=${intendedDriverId}`);
      } else if (result.userType === 'driver') {
        navigate('/driver/dashboard');
      } else {
        navigate('/rider/dashboard');
      }
    } else {
      setError(result.error);
    }
    
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#4a7c8a] to-[#1f2937]">
      <div className="bg-white p-8 rounded-lg shadow-2xl w-full max-w-md">
        <div className="text-center mb-6">
          <img src="/logo.png" alt="RideHowl" className="h-16 w-16 mx-auto mb-2" />
          <h1 className="text-2xl font-bold" style={{ color: '#4a7c8a' }}>RideHowl</h1>
        </div>
        <h2 className="text-xl font-semibold text-center text-gray-800 mb-6">
          Welcome Back
        </h2>
        
        {/* User Type Selection */}
        <div className="flex gap-4 mb-6">
          <button
            type="button"
            onClick={() => setUserType('rider')}
            className={`flex-1 py-2 px-4 rounded-lg font-medium transition ${
              userType === 'rider'
                ? 'bg-[#4a7c8a] text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Rider
          </button>
          <button
            type="button"
            onClick={() => setUserType('driver')}
            className={`flex-1 py-2 px-4 rounded-lg font-medium transition ${
              userType === 'driver'
                ? 'bg-[#4a7c8a] text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Driver
          </button>
        </div>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-medium mb-2">
              Email or Phone Number
            </label>
            <input
              type="text"
              name="username"
              value={formData.username}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4a7c8a]"
              required
            />
          </div>

          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-medium mb-2">
              Password
            </label>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4a7c8a]"
              required
            />
          </div>

          <div className="mb-6">
            <label className="flex items-center">
              <input
                type="checkbox"
                name="rememberMe"
                checked={formData.rememberMe}
                onChange={handleChange}
                className="mr-2"
              />
              <span className="text-sm text-gray-700">Remember me for 30 days</span>
            </label>
          </div>

          <div className="text-sm mb-4">
            <Link 
              to="/forgot-password" 
              className="font-medium text-indigo-600 hover:text-indigo-500"
            >
              Forgot your password?
            </Link>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-[#4a7c8a] text-white py-3 rounded-lg font-medium hover:bg-[#5a9aa8] transition disabled:opacity-50"
          >
            {loading ? 'Logging in...' : 'Login'}
          </button>
        </form>

        <div className="mt-6 text-center">
          {userType === 'driver' && isNativeApp() ? (
            <div className="text-gray-600 text-sm">
              <p>Don't have a driver account?</p>
              <p className="mt-1 font-medium text-[#4a7c8a]">
                Please visit app.ridehowl.com in your browser to register as a driver.
              </p>
            </div>
          ) : (
            <p className="text-gray-600">
              Don't have an account?{' '}
              <Link
                to={userType === 'driver' ? '/signup/driver' : '/signup/rider'}
                className="text-[#4a7c8a] hover:underline font-medium"
              >
                Sign up as {userType}
              </Link>
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default Login;